import type { ElementType, MouseEventHandler, ReactNode, RefObject } from 'react';
import type { To } from 'react-router-dom';
import { createContext, useContext, useMemo } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { TooltipProps } from '../../components/Tooltip/Tooltip';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { ControlSize } from '../shared/types';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import { stateZIndexes } from '../../common/z_indexes';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { ControlProvider, useControlSize } from '../shared/context';
import { selectors, transitions } from '../shared/styles';

type SegmentsAppearance = 'box' | 'indicator';
type SegmentsDirection = 'column' | 'row';

type SegmentsContextValue = {
  direction?: SegmentsDirection;
  appearance: SegmentsAppearance;
};

const SegmentsContext = createContext<SegmentsContextValue | undefined>(undefined);

type SegmentsProviderProps = {
  appearance?: SegmentsAppearance;
  children: ReactNode;
  direction?: SegmentsDirection;
};

export function SegmentsProvider({
  appearance = 'box',
  children,
  direction,
}: SegmentsProviderProps) {
  const value = useMemo(() => ({ direction, appearance }), [direction, appearance]);
  return <SegmentsContext.Provider value={value}>{children}</SegmentsContext.Provider>;
}

export const useSegmentsDirection = (
  controlledValue?: SegmentsDirection,
  defaultValue: SegmentsDirection = 'row',
) => {
  const segments = useContext(SegmentsContext);
  return controlledValue ?? segments?.direction ?? defaultValue;
};

const useSegmentsAppearance = () => useContext(SegmentsContext)?.appearance ?? 'box';

export const SegmentIcon = styled(Icon, {
  color: '$$segmentIconColor',

  variants: {
    direction: {
      column: {},
      row: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
      large: {},
      'x-large': {},
    },
  },
  compoundVariants: [
    {
      direction: 'column',
      size: 'x-small',
      css: {
        width: '$16',
        height: '$16',
      },
    },
    {
      direction: 'column',
      size: 'small',
      css: {
        width: '$20',
        height: '$20',
      },
    },
    {
      direction: 'column',
      size: 'medium',
      css: {
        width: '$28',
        height: '$28',
      },
    },
    {
      direction: 'column',
      size: 'large',
      css: {
        width: '$32',
        height: '$32',
      },
    },
    {
      direction: 'column',
      size: 'x-large',
      css: {
        width: '$40',
        height: '$40',
      },
    },
    {
      direction: 'row',
      size: 'x-small',
      css: {
        width: '$12',
        height: '$12',
      },
    },
    {
      direction: 'row',
      size: 'small',
      css: {
        width: '$12',
        height: '$12',
      },
    },
    {
      direction: 'row',
      size: 'medium',
      css: {
        width: '$14',
        height: '$14',
      },
    },
    {
      direction: 'row',
      size: 'large',
      css: {
        width: '$16',
        height: '$16',
      },
    },
    {
      direction: 'row',
      size: 'x-large',
      css: {
        width: '$16',
        height: '$16',
      },
    },
  ],
});
SegmentIcon.displayName = 'SegmentIcon';

export const SegmentLabel = styled(Text, {
  color: '$$segmentLabelColor',
  fontWeight: '$bold',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...BodySansSizes,
      },
      'x-large': {
        ...LargeSansSizes,
      },
    },
  },
});
SegmentLabel.displayName = 'SegmentLabel';

const SegmentContainer = styled('button', {
  position: 'relative',
  zIndex: stateZIndexes.base,
  display: 'flex',
  minWidth: 0,
  justifyContent: 'center',
  alignItems: 'center',
  transition: transitions.control,

  [selectors.hover]: {
    zIndex: stateZIndexes.hover,
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    selected: {
      true: {
        zIndex: stateZIndexes.focus,

        '&:after': {
          display: 'none',
        },
      },
      false: {
        zIndex: stateZIndexes.base,
      },
    } as const,
    appearance: {
      box: {
        borderRadius: '$8',
      },
      indicator: {
        '&::before': {
          content: '',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '$2',
          backgroundColor: 'transparent',
        },

        [selectors.focus]: {
          borderRadius: '$8',
        },
      },
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {},
      'leading-label': {},
    },
    direction: {
      column: {},
      row: {},
    },
    disabled: {
      true: {
        background: colors.bgNeutralLight,
        borderRadius: 0,
        opacity: 0.7,
        cursor: 'not-allowed',

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },

        '&:first-child': {
          borderTopLeftRadius: '$8',
          borderBottomLeftRadius: '$8',
        },

        '&:last-child': {
          borderTopRightRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
      false: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
      large: {},
      'x-large': {},
    },
    variant: {
      destructive: {},
      primary: {},
    },
  },

  compoundVariants: [
    {
      direction: 'column',
      arrangement: 'leading-icon',
      css: {
        flexDirection: 'column',
      },
    },
    {
      direction: 'column',
      arrangement: 'leading-label',
      css: {
        flexDirection: 'column-reverse',
      },
    },
    {
      direction: 'row',
      arrangement: 'leading-icon',
      css: {
        flexDirection: 'row',
      },
    },
    {
      direction: 'row',
      arrangement: 'leading-label',
      css: {
        flexDirection: 'row-reverse',
      },
    },
    {
      direction: 'row',
      size: 'small',
      css: {
        minWidth: '$28',
        minHeight: '$24',
        padding: '$4 $6',
      },
    },
    {
      direction: 'row',
      size: 'medium',
      css: {
        minWidth: '$32',
        minHeight: '$28',
        padding: '$4 $8',
      },
    },
    {
      direction: 'row',
      size: 'large',
      css: {
        minWidth: '$40',
        minHeight: '$36',
        padding: '$8 $12',
      },
    },
    {
      direction: 'row',
      size: 'x-large',
      css: {
        minWidth: '$44',
        minHeight: '$40',
        padding: '$8 $12',
      },
    },
    {
      direction: 'column',
      size: 'small',
      css: {
        padding: '$6 $8 $4',
      },
    },
    {
      direction: 'column',
      size: 'medium',
      css: {
        padding: '$8 $12',
      },
    },
    {
      direction: 'column',
      size: 'large',
      css: {
        padding: '$12 $16',
      },
    },
    {
      direction: 'column',
      size: 'x-large',
      css: {
        padding: '$12 $16',
      },
    },
    {
      appearance: 'indicator',
      direction: 'row',
      size: 'small',
      css: {
        padding: '$4 0',
      },
    },
    {
      appearance: 'indicator',
      direction: 'row',
      size: 'medium',
      css: {
        padding: '$4 0',
      },
    },
    {
      appearance: 'indicator',
      direction: 'row',
      size: 'large',
      css: {
        padding: '$8 0',
      },
    },
    {
      appearance: 'indicator',
      direction: 'row',
      size: 'x-large',
      css: {
        padding: '$8 0',
      },
    },
    {
      selected: true,
      appearance: 'box',
      variant: 'primary',
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        $$segmentLabelColor: colors.headingBrandLight,
        $$segmentIconColor: colors.bodyBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
          $$segmentLabelColor: colors.headingBrandDark,
          $$segmentIconColor: colors.bodyBrandDark,
        },

        [selectors.hover]: {
          $$segmentLabelColor: colors.headingBrandLight,
          $$segmentIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingBrandDark,
            $$segmentIconColor: colors.bodyBrandDark,
          },
        },
      },
    },
    {
      selected: false,
      variant: 'primary',
      css: {
        [selectors.hover]: {
          $$segmentLabelColor: colors.headingNeutralLight,
          $$segmentIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingNeutralDark,
            $$segmentIconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
    {
      selected: true,
      appearance: 'box',
      variant: 'destructive',
      css: {
        background: colors.bgNegativeLight,
        strokeAll: colors.strokeNegativeLight,
        $$segmentLabelColor: colors.headingNegativeLight,
        $$segmentIconColor: colors.bodyNegativeLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgNegativeDark,
          strokeAll: colors.strokeNegativeDark,
          $$segmentLabelColor: colors.headingNegativeDark,
          $$segmentIconColor: colors.bodyNegativeDark,
        },

        [selectors.hover]: {
          $$segmentLabelColor: colors.headingNegativeLight,
          $$segmentIconColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingNegativeDark,
            $$segmentIconColor: colors.bodyNegativeDark,
          },
        },
      },
    },
    {
      selected: false,
      variant: 'destructive',
      css: {
        [selectors.hover]: {
          $$segmentLabelColor: colors.headingNegativeLight,
          $$segmentIconColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingNegativeDark,
            $$segmentIconColor: colors.bodyNegativeDark,
          },
        },
      },
    },
    {
      selected: true,
      appearance: 'indicator',
      variant: 'primary',
      css: {
        $$segmentLabelColor: colors.headingBrandLight,
        $$segmentIconColor: colors.bodyBrandLight,

        '&:before': {
          backgroundColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.headingBrandDark,
          },
        },

        [selectors.hover]: {
          $$segmentLabelColor: colors.headingBrandLight,
          $$segmentIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingBrandDark,
            $$segmentIconColor: colors.bodyBrandDark,
          },
        },
      },
    },
    {
      selected: false,
      appearance: 'indicator',
      variant: 'primary',
      css: {
        [selectors.hover]: {
          $$segmentLabelColor: colors.headingNeutralLight,
          $$segmentIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$segmentLabelColor: colors.headingNeutralDark,
            $$segmentIconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
  ],
});
SegmentContainer.displayName = 'SegmentContainer';

type SegmentArrangement = 'hidden-label' | 'leading-icon' | 'leading-label';
type SegmentDirection = 'column' | 'row';
type SegmentSize = ControlSize;
export interface SegmentProps {
  /**
   * Show a feature badge (e.g. internal, alpha, beta) next to the segment content.
   * Renders a small badge indicating the access level of the feature.
   */
  accessLevel?: AccessLevelProp;
  /**
   * Layout of icon and label within the segment.
   * - `leading-icon`: Icon before label (default)
   * - `leading-label`: Label before icon
   * - `hidden-label`: Icon only; label appears in tooltip on hover
   */
  arrangement?: SegmentArrangement;
  /**
   * Layout direction of icon and label: `row` (horizontal) or `column` (vertical).
   * Inherits from `Segments` when not specified.
   */
  direction?: SegmentDirection;
  /**
   * When true, the segment is non-interactive and visually muted.
   * Disables onClick, routing, and keyboard focus.
   */
  disabled?: boolean;
  /**
   * Icon to display. Use with arrangement to control placement relative to the label.
   */
  icon?: IconName;
  /**
   * Label to display. With `hidden-label` arrangement, the label is shown in the
   * tooltip instead of being rendered inline.
   */
  label: string;
  /**
   * When true, the segment appears selected (highlighted). Use with onClick for
   * controlled selection state in a segment group.
   */
  selected?: boolean;
  /**
   * Visual size: `x-small`, `small`, `medium`, `large`, or `x-large`.
   * Defaults to `medium` when omitted. Inherits from `Segments` when provided there.
   */
  size?: SegmentSize;
  /**
   * Tooltip props to wrap the segment. When using `hidden-label`, the label
   * becomes the tooltip content unless you pass custom `contents`.
   */
  tooltip?: TooltipProps;
  /**
   * Visual style: `primary` (default) or `destructive`.
   * Use `destructive` for dangerous or irreversible actions.
   */
  variant?: 'destructive' | 'primary';
  /**
   * Routing
   */
  as?: ElementType;
  to?: To;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  ref?: RefObject<HTMLButtonElement>;
}

const getSegmentDirection = (arrangement: SegmentArrangement, direction: SegmentDirection) => {
  if (arrangement === 'leading-icon' && direction === 'column') {
    return 'column';
  }
  if (arrangement === 'leading-label' && direction === 'column') {
    return 'column-reverse';
  }
  if (arrangement === 'leading-label' && direction === 'row') {
    return 'row-reverse';
  }
  return 'row';
};

const getSegmentGap = (direction: SegmentDirection, size: SegmentSize) => {
  if (direction === 'column') {
    switch (size) {
      case 'x-small':
      case 'small':
        return 2;
    }
  }
  return 4;
};

const getSegmentPreset = (size: SegmentSize) => {
  switch (size) {
    case 'x-small':
    case 'small':
      return 'small';
    case 'x-large':
      return 'large';
  }
  return 'body';
};

export function Segment({
  accessLevel,
  arrangement = 'leading-icon',
  disabled = false,
  direction,
  icon,
  label,
  selected = false,
  size,
  tooltip,
  variant = 'primary',
  // Routing
  as,
  to,
  onClick,
  ref,
  ...remaining
}: SegmentProps) {
  const controlSize = useControlSize(size, 'medium');
  const segmentsDirection = useSegmentsDirection(direction, 'row');
  const appearance = useSegmentsAppearance();

  const render = (
    <SegmentContainer
      type="button"
      tabIndex={disabled ? -1 : undefined}
      role="tab"
      as={disabled ? undefined : as}
      to={disabled ? undefined : to}
      onClick={disabled ? undefined : onClick}
      aria-selected={selected ? 'true' : 'false'}
      selected={selected}
      appearance={appearance}
      arrangement={arrangement}
      direction={segmentsDirection}
      size={controlSize}
      variant={variant}
      disabled={disabled}
      {...remaining}
      ref={ref}
    >
      <AlignStack
        aria-label={arrangement === 'hidden-label' ? label : undefined}
        direction={getSegmentDirection(arrangement, segmentsDirection)}
        gap={getSegmentGap(segmentsDirection, controlSize)}
        width="fit-content"
        preset={getSegmentPreset(controlSize)}
        start={icon && <SegmentIcon icon={icon} direction={segmentsDirection} size={controlSize} />}
        end={accessLevel && <FeatureBadge type={accessLevel} size="x-small" />}
      >
        {arrangement !== 'hidden-label' && label && (
          <SegmentLabel size={controlSize}>{label}</SegmentLabel>
        )}
      </AlignStack>
    </SegmentContainer>
  );

  if (tooltip || arrangement === 'hidden-label') {
    return (
      <Tooltip {...tooltip} contents={tooltip?.contents ?? label}>
        {render}
      </Tooltip>
    );
  }

  return render;
}

const SegmentsDivider = styled(Divider, {
  position: 'relative',
  zIndex: 0,

  variants: {
    size: {
      'x-small': {
        padding: '$4 0',
      },
      small: {
        padding: '$6 0',
      },
      medium: {
        padding: '$8 0',
      },
      large: {
        padding: '$10 0',
      },
      'x-large': {
        padding: '$12 0',
      },
    },
  },
});
SegmentsDivider.displayName = 'SegmentsDivider';

const SegmentsContainer = styled('div', {
  display: 'flex',
  width: '100%',
  background: colors.bgApplicationLight,
  strokeAll: colors.strokeNeutralLight,
  borderRadius: '$8',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    strokeAll: colors.strokeNeutralDark,
  },

  variants: {
    appearance: {
      box: {},
      indicator: {
        background: 'transparent',
        strokeAll: 'none',
        borderRadius: 0,
        gap: '$8',
        strokeBottom: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          background: 'transparent',
          strokeAll: 'none',
          strokeBottom: colors.strokeNeutralDark,
        },
      },
    },
    contentMode: {
      fit: {
        width: 'fit-content',
      },
      stretch: {
        width: '100%',
      },
    },
    order: {
      'first-to-last': {
        flexDirection: 'row',
      },
      'last-to-first': {
        flexDirection: 'row-reverse',
      },
    },
    proportions: {
      equal: {
        [`& > ${SegmentContainer}`]: {
          flex: 1,
        },
      },
      fit: {},
    },
  },
});
SegmentsContainer.displayName = 'SegmentsContainer';

export type SegmentsOrder = 'first-to-last' | 'last-to-first';

export type SegmentsProps = {
  /**
   * Visual style of the segments container: `box` (filled background) or
   * `indicator` (underline on selected).
   */
  appearance?: SegmentsAppearance;
  /**
   * How content fills the container: `fit` (shrink to content) or
   * `stretch` (fill available space).
   */
  contentMode?: 'fit' | 'stretch';
  /**
   * Layout direction of segments: `row` (horizontal) or `column` (vertical).
   * Passed to child segments via context.
   */
  direction?: SegmentsDirection;
  /**
   * Order of segments: `first-to-last` or `last-to-first`.
   */
  order?: SegmentsOrder;
  /**
   * How segments share space: `equal` (same width) or `fit` (shrink to content).
   */
  proportions?: 'equal' | 'fit';
  /**
   * Array of segment configs. Renders as `Segment` components with dividers.
   */
  segments: SegmentProps[];
  /**
   * Visual size: `x-small`, `small`, `medium`, `large`, or `x-large`.
   * Passed to child segments via context.
   */
  size?: SegmentSize;
};

export function Segments({
  appearance = 'box',
  contentMode = 'stretch',
  direction = 'row',
  order = 'first-to-last',
  proportions = 'equal',
  segments,
  size = 'medium',
  ...remaining
}: SegmentsProps) {
  const segment = (props: SegmentProps, index: number) => (
    <Segment key={props.label ?? index} {...props} />
  );

  let content: ReactNode = null;
  if (segments?.length) {
    if (appearance === 'indicator') {
      content = segments.map((props, i) => segment(props, i));
    } else {
      content = segments.flatMap((props, index) =>
        index < segments.length - 1
          ? [
              segment(props, index),
              <SegmentsDivider
                key={`segments-divider-${props.label ?? index}`}
                appearance="thin"
                direction="column"
                size={size}
                variant="neutral"
              />,
            ]
          : [segment(props, index)],
      );
    }
  }

  return (
    <SegmentsContainer
      role="tablist"
      appearance={appearance}
      contentMode={contentMode}
      order={order}
      proportions={proportions}
      {...remaining}
    >
      <SegmentsProvider direction={direction} appearance={appearance}>
        <ControlProvider value={{ size }}>{content}</ControlProvider>
      </SegmentsProvider>
    </SegmentsContainer>
  );
}
